import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Modal,
  SidePanel,
  type BadgeVariant,
  type ButtonVariant,
} from '@silverfalls/sterling';
import { DetailRow } from '../components/DetailRow.js';
import { useToast } from '../components/Toast.js';
import {
  adminLegalNextRequisitionStates,
  convertRequisitionToPO,
  fetchRequisition,
  fetchVendors,
  updateRequisition,
  updateRequisitionState,
  type RequisitionRow,
  type RequisitionState,
  type UpdateRequisitionInput,
  type VendorRow,
} from '../api/p2p.js';

const REQUESTING_USER = 'u-ui-skeleton';

export function RequisitionDetail(): JSX.Element {
  const { ref } = useParams<{ ref: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [req, setReq] = useState<RequisitionRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transitioning, setTransitioning] = useState<RequisitionState | null>(null);
  const [converting, setConverting] = useState(false);

  const load = useCallback(async () => {
    if (!ref) return;
    setLoading(true);
    setError(null);
    try {
      const row = await fetchRequisition(ref);
      setReq(row);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [ref]);

  useEffect(() => {
    void load();
  }, [load]);

  const onConvert = useCallback(async () => {
    if (!req) return;
    if (
      !confirm(
        `Convert this requisition into purchase orders? One PO will be created per distinct vendor on the lines.`,
      )
    ) {
      return;
    }
    setConverting(true);
    try {
      const result = await convertRequisitionToPO({
        requisitionRef: req.requisitionRef,
        requestingUser: REQUESTING_USER,
      });
      if (result.__typename === 'ConvertRequisitionToPOFailure') {
        toast.push(`${result.class}: ${JSON.stringify(result.details)}`, 'error');
      } else {
        const summary = result.purchaseOrders
          .map((p) => p.poRef)
          .join(', ');
        toast.push(
          `Converted → ${result.purchaseOrders.length} PO${result.purchaseOrders.length === 1 ? '' : 's'}: ${summary}`,
          'success',
        );
        // If exactly one PO was minted, jump to it; otherwise refresh the
        // requisition (now CONVERTED) so the user can see the final state.
        if (result.purchaseOrders.length === 1 && result.purchaseOrders[0]) {
          navigate(`/purchase-orders/${result.purchaseOrders[0].poRef}`);
        } else {
          await load();
        }
      }
    } catch (err) {
      toast.push(err instanceof Error ? err.message : String(err), 'error');
    } finally {
      setConverting(false);
    }
  }, [req, load, navigate, toast]);

  // Destructive transitions get a Sterling type-to-confirm modal
  // (REJECT / WITHDRAW). Other transitions go through immediately.
  const [pendingDestructive, setPendingDestructive] = useState<{
    toState: RequisitionState;
    reason: string;
  } | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const doTransition = useCallback(
    async (toState: RequisitionState, reason?: string) => {
      if (!req) return;
      setTransitioning(toState);
      try {
        const result = await updateRequisitionState({
          requisitionRef: req.requisitionRef,
          toState,
          ...(reason ? { reason } : {}),
          requestingUser: REQUESTING_USER,
        });
        if (result.__typename === 'UpdateRequisitionStateFailure') {
          toast.push(`${result.class}: ${JSON.stringify(result.details)}`, 'error');
        } else {
          toast.push(`${result.fromState} → ${result.toState}`, 'success');
          await load();
        }
      } catch (err) {
        toast.push(err instanceof Error ? err.message : String(err), 'error');
      } finally {
        setTransitioning(null);
      }
    },
    [req, load, toast],
  );

  const onTransition = useCallback(
    async (toState: RequisitionState) => {
      if (!req) return;
      if (toState === 'REJECTED' || toState === 'WITHDRAWN') {
        setPendingDestructive({ toState, reason: '' });
        return;
      }
      await doTransition(toState);
    },
    [req, doTransition],
  );

  if (loading) return <div className="t-body-sm">Loading requisition…</div>;
  if (error) {
    return (
      <div className="alert alert-neg" role="alert">
        Couldn't load requisition: {error}
      </div>
    );
  }
  if (!req) {
    return (
      <div className="empty-state">
        <p>Requisition not found: {ref}</p>
        <p>
          <Link to="/requisitions">Back to requisitions</Link>
        </p>
      </div>
    );
  }

  const nextStates = adminLegalNextRequisitionStates(req.state);

  return (
    <section className="vendor-detail">
      <div className="screen-header">
        <div className="entity-header">
          <Link to="/requisitions" className="back-link">
            ← Requisitions
          </Link>
          <h2>{req.justification || 'Requisition'}</h2>
          <div className="entity-ref mono">{req.requisitionRef}</div>
          <div className="entity-summary">
            <span className="entity-summary-part">
              <Badge variant={stateBadgeVariant(req.state)} dot={false}>
                {req.state}
              </Badge>
            </span>
            <span className="entity-summary-part">{req.opcoRef}</span>
            <span className="entity-summary-part">by {req.requestingUser}</span>
            <span className="entity-summary-part mono">
              {req.estimatedTotal} {req.currencyCode}
            </span>
          </div>
        </div>
        <div className="header-right-stack">
          <div className="state-actions">
            {req.state === 'SUBMITTED' && (
              <Button
                variant="secondary"
                size="sm"
                icon="pencil"
                onClick={() => setEditOpen(true)}
                disabled={transitioning !== null || converting}
              >
                Edit
              </Button>
            )}
            {nextStates.map((next) => (
              <Button
                key={next}
                variant={actionVariant(next)}
                size="sm"
                onClick={() => onTransition(next)}
                disabled={transitioning !== null || converting}
                loading={transitioning === next}
              >
                {actionLabel(next)}
              </Button>
            ))}
            {req.state === 'APPROVED' && (
              <Button
                variant="primary"
                size="sm"
                icon="arrow-right"
                onClick={onConvert}
                disabled={converting || transitioning !== null}
                loading={converting}
                title="Mint one PO per distinct vendor and mark this requisition CONVERTED"
              >
                Convert to PO
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-card">
          <h3 className="detail-card-title">Header</h3>
          <div className="detail-rows">
            <DetailRow label="OPCO" value={req.opcoRef} mono />
            <DetailRow label="Requester" value={req.requestingUser} mono />
            <DetailRow label="Currency" value={req.currencyCode} mono />
            <DetailRow label="Estimated total" value={req.estimatedTotal} mono />
            <DetailRow
              label="Suggested vendor"
              value={req.suggestedVendor?.legalName ?? '—'}
            />
            {req.suggestedVendorRef && (
              <DetailRow label="Vendor ref" value={req.suggestedVendorRef} mono />
            )}
            <DetailRow label="Notes" value={req.notes ?? '—'} />
            <DetailRow label="Submitted at" value={formatDateTime(req.createdAt)} mono />
          </div>
        </div>
      </div>

      <div className="po-lines">
        <div className="po-lines-header">
          <span className="form-label">Lines ({req.lines.length})</span>
        </div>
        <table className="po-lines-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Product</th>
              <th>UoM</th>
              <th>Qty</th>
              <th>Est. unit price</th>
              <th>Est. line total</th>
              <th>GL</th>
              <th>Suggested vendor</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {req.lines.map((line) => (
              <tr key={line.requisitionLineRef}>
                <td className="mono">{line.lineNumber}</td>
                <td>{line.productName}</td>
                <td className="mono">{line.unitOfMeasure}</td>
                <td className="mono">{line.quantity}</td>
                <td className="mono">{line.estimatedUnitPrice}</td>
                <td className="mono">{line.estimatedLineTotal}</td>
                <td className="mono">{line.defaultGLAccount}</td>
                <td className="mono">
                  {line.suggestedVendorRef ? line.suggestedVendorRef.slice(0, 16) + '…' : '—'}
                </td>
                <td>{line.notes ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        open={pendingDestructive !== null}
        title={
          pendingDestructive?.toState === 'REJECTED'
            ? `Reject ${req.requisitionRef.slice(0, 16)}…`
            : `Withdraw ${req.requisitionRef.slice(0, 16)}…`
        }
        variant={pendingDestructive?.toState === 'REJECTED' ? 'danger' : 'default'}
        confirmLabel={
          pendingDestructive?.toState === 'REJECTED' ? 'Reject requisition' : 'Withdraw'
        }
        {...(pendingDestructive?.toState === 'REJECTED'
          ? { typeToConfirm: req.requisitionRef }
          : {})}
        onCancel={() => setPendingDestructive(null)}
        onConfirm={() => {
          const pd = pendingDestructive;
          setPendingDestructive(null);
          if (pd) {
            void doTransition(pd.toState, pd.reason || undefined);
          }
        }}
      >
        {pendingDestructive?.toState === 'REJECTED'
          ? 'Rejecting marks this requisition as terminal — it can\'t be re-opened. The requester will see the reason in the state-transition history. Type the requisition ref to confirm.'
          : 'Withdrawing marks this requisition as terminal. The requester pulled it back; no PO will result.'}
      </Modal>

      {editOpen && (
        <EditRequisitionDialog
          req={req}
          onClose={() => setEditOpen(false)}
          onSaved={(changedFields) => {
            setEditOpen(false);
            if (changedFields.length === 0) toast.push('No changes', 'info');
            else toast.push(`Saved ${changedFields.length} change(s)`, 'success');
            void load();
          }}
          onError={(message) => toast.push(message, 'error')}
        />
      )}
    </section>
  );
}

interface EditRequisitionDialogProps {
  readonly req: RequisitionRow;
  readonly onClose: () => void;
  readonly onSaved: (changedFields: ReadonlyArray<string>) => void;
  readonly onError: (message: string) => void;
}

function EditRequisitionDialog({
  req,
  onClose,
  onSaved,
  onError,
}: EditRequisitionDialogProps): JSX.Element {
  const [justification, setJustification] = useState(req.justification ?? '');
  const [suggestedVendorRef, setSuggestedVendorRef] = useState(req.suggestedVendorRef ?? '');
  const [notes, setNotes] = useState(req.notes ?? '');
  const [vendors, setVendors] = useState<ReadonlyArray<VendorRow>>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await fetchVendors(req.opcoRef);
        if (cancelled) return;
        setVendors(rows.filter((v) => v.state === 'ACTIVE'));
      } catch {
        // Silent — the existing suggested-vendor stays selectable as a text value.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [req.opcoRef]);

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setSubmitting(true);
      setError(null);

      type MutableInput = {
        -readonly [K in keyof UpdateRequisitionInput]: UpdateRequisitionInput[K];
      };
      const working: MutableInput = {
        requisitionRef: req.requisitionRef,
        requestingUser: REQUESTING_USER,
      };
      const clear: Array<'justification' | 'suggestedVendorRef' | 'notes'> = [];
      const apply = (
        key: 'justification' | 'suggestedVendorRef' | 'notes',
        next: string,
        current: string | null | undefined,
      ): void => {
        const trimmed = next.trim();
        const cur = current ?? '';
        if (trimmed === cur) return;
        if (trimmed === '') {
          clear.push(key);
        } else {
          (working as Record<string, unknown>)[key] = trimmed;
        }
      };
      apply('justification', justification, req.justification);
      apply('suggestedVendorRef', suggestedVendorRef, req.suggestedVendorRef);
      apply('notes', notes, req.notes);
      if (clear.length > 0) working.clear = clear;

      try {
        const result = await updateRequisition(working);
        if (result.__typename === 'UpdateRequisitionFailure') {
          const msg = `${result.class}: ${JSON.stringify(result.details)}`;
          setError(msg);
          onError(msg);
          setSubmitting(false);
          return;
        }
        onSaved(result.changedFields);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        onError(msg);
        setSubmitting(false);
      }
    },
    [
      req,
      justification,
      suggestedVendorRef,
      notes,
      onSaved,
      onError,
    ],
  );

  return (
    <SidePanel
      open
      title={`Edit requisition`}
      width={640}
      onClose={onClose}
      onSave={() => formRef.current?.requestSubmit()}
      dirty={!submitting}
      saveLabel={submitting ? 'Saving…' : 'Save changes'}
    >
      <form ref={formRef} className="form-grid" onSubmit={onSubmit}>
        <label className="form-row form-row-full">
          <span className="form-label">Justification</span>
          <input
            type="text"
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="Why this requisition? — empty to clear"
          />
        </label>
        <label className="form-row">
          <span className="form-label">Suggested vendor</span>
          <select
            value={suggestedVendorRef}
            onChange={(e) => setSuggestedVendorRef(e.target.value)}
          >
            <option value="">— None —</option>
            {vendors.map((v) => (
              <option key={v.vendorRef} value={v.vendorRef}>
                {v.legalName}
              </option>
            ))}
            {suggestedVendorRef !== '' &&
              !vendors.find((v) => v.vendorRef === suggestedVendorRef) && (
                <option value={suggestedVendorRef}>{suggestedVendorRef}</option>
              )}
          </select>
        </label>
        <label className="form-row form-row-full">
          <span className="form-label">Notes</span>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Internal notes — empty to clear"
          />
        </label>
        {error && <div className="alert alert-neg" role="alert">{error}</div>}
      </form>
    </SidePanel>
  );
}

function stateBadgeVariant(state: RequisitionState): BadgeVariant {
  switch (state) {
    case 'APPROVED':
    case 'CONVERTED':
      return 'success';
    case 'SUBMITTED':
      return 'info';
    case 'WITHDRAWN':
      return 'neutral';
    case 'REJECTED':
      return 'warning';
  }
}

function formatDateTime(iso: string): string {
  return new Date(iso).toISOString().slice(0, 19).replace('T', ' ');
}


function actionLabel(toState: RequisitionState): string {
  switch (toState) {
    case 'APPROVED':
      return 'Approve';
    case 'REJECTED':
      return 'Reject';
    case 'WITHDRAWN':
      return 'Withdraw';
    case 'SUBMITTED':
      return 'Return to submitted';
    case 'CONVERTED':
      return 'Convert';
  }
}

function actionVariant(toState: RequisitionState): ButtonVariant {
  switch (toState) {
    case 'APPROVED':
    case 'CONVERTED':
      return 'primary';
    case 'REJECTED':
      return 'destructive';
    case 'WITHDRAWN':
      return 'secondary';
    case 'SUBMITTED':
      return 'secondary';
  }
}
